import React, { useEffect, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { setDepositSourceForPath } from 'helpers/postHogDepositSource';

import { PromotionCard } from '@atlas/components';
import { Bus, getPromotion, isEmptyObject, SiteConfigManager, useSettings } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Button, Icon, PromoDetailSkeleton, ScrollableContainer } from '@atlas/ui';

import { POSTHOG_DEPOSIT_SOURCE } from 'common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'common/utils';
import SeoPageText from 'src/components/SeoPage/SeoPageText';

import PromotionPageLayout from './PromotionPageLayout';

const PromotionsDetail = () => {
  const {
    params: { id },
  } = useRouteMatch();
  const { lang } = useSettings();

  const promotionsURL = SiteConfigManager.getComponentPath(COMPONENT_TYPES.PROMOTIONS);

  const [loading, setLoading] = useState(true);
  const [promotion, setPromotion] = useState(null);

  useEffect(() => {
    if (id) {
      Bus.emit('ui:scroll:content', { behavior: 'auto', scrollTo: 0 });
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getPromotion(id)
      .then(data => {
        if (isEmptyObject(data)) throw new Error('Promotion not found');
        setPromotion(data);
      })
      .catch(() => setPromotion(null))
      .finally(() => setLoading(false));
  }, [id, lang]);

  if (loading) {
    return (
      <PromotionPageLayout>
        <PromoDetailSkeleton />
      </PromotionPageLayout>
    );
  }

  if (!promotion) {
    return (
      <PromotionPageLayout>
        <div className="promotions-details__error">Promotion not found</div>
      </PromotionPageLayout>
    );
  }

  return (
    <PromotionPageLayout className="promotions-page-details">
      <div className="promotions-details">
        <Link className="header-title ellipsis" to={promotionsURL}>
          <Icon className="icon-back" name="back" />
          <span className="header-title__name">{promotion.title}</span>
        </Link>

        <div className="promotions-details-card">
          <PromotionCard
            description={promotion.description}
            imageUrl={imageUrlCDN(promotion.cover_image_url, CDN_TYPE_CMS)}
            isPromotionChosen
            layoutType={promotion.layout}
          />
          {promotion.link && (
            <div className="promotions-details-link-wrapper">
              <Link
                className="promotions-details-link"
                onClick={() =>
                  setDepositSourceForPath(
                    POSTHOG_DEPOSIT_SOURCE.PROMO_DETAILED_PAGE,
                    promotion.link,
                  )
                }
                to={promotion.link}
              >
                <Button
                  label={promotion.button || 'promo_button_empty'}
                  size="big"
                  type="primary"
                />
              </Link>
            </div>
          )}
        </div>

        <ScrollableContainer className="tournaments__detail" id="content">
          <div className="promotions-details-content-wrapper">
            <div className="promotions-details-content">
              <div className="tournaments-box">
                <div className="tournaments-box__inner">
                  <div
                    className="tournaments-box__text static-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(promotion.short_content),
                    }}
                  />
                </div>
              </div>
              {promotion.full_content && (
                <div className="tournaments-box">
                  <div className="tournaments-box__inner static-content">
                    <div
                      className="tournaments-box__text"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(promotion.full_content),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </ScrollableContainer>
      </div>
      <SeoPageText />
    </PromotionPageLayout>
  );
};

export default PromotionsDetail;
